import { useState, useCallback, useRef } from 'react'
import { X, Upload, Image as ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '../utils/api'

interface ImageUnderstandingModalProps {
  isOpen: boolean
  onClose: () => void
  onPromptGenerated: (prompt: string) => void
}

export function ImageUnderstandingModal({ isOpen, onClose, onPromptGenerated }: ImageUnderstandingModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isUnderstanding, setIsUnderstanding] = useState(false)
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((file: File) => {
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件')
      return
    }

    // 验证文件大小 (最大 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('图片文件不能超过 10MB')
      return
    }

    setSelectedFile(file)
    setError('')
    setGeneratedPrompt('')

    // 生成预览
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleUploadAndUnderstand = useCallback(async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setIsUnderstanding(true)
    setError('')

    try {
      console.log('Starting image understanding...')
      const response = await apiClient.understandImage(selectedFile)

      console.log('API Response:', response)

      // 后端返回的数据是扁平结构，直接在根级别
      if (response.success && response.prompt) {
        setGeneratedPrompt(response.prompt)
        console.log('Image understanding completed:', response.prompt)
      } else {
        throw new Error(response.error || '图像理解失败')
      }
    } catch (err) {
      console.error('Image understanding error:', err)
      setError(err instanceof Error ? err.message : '图像理解失败，请重试')
    } finally {
      setIsUploading(false)
      setIsUnderstanding(false)
    }
  }, [selectedFile])

  const handleUsePrompt = useCallback(() => {
    if (generatedPrompt) {
      onPromptGenerated(generatedPrompt)
      handleClose()
    }
  }, [generatedPrompt, onPromptGenerated])

  const handleClose = useCallback(() => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setGeneratedPrompt('')
    setError('')
    setIsUploading(false)
    setIsUnderstanding(false)
    onClose()
  }, [onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[#161b22] rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-cyan-900/30">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cyan-900/30">
          <div className="flex items-center space-x-3">
            <ImageIcon className="w-6 h-6 text-cyan-400" />
            <h2 className="text-2xl font-bold text-white">图片理解</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Upload Area */}
          {!previewUrl && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-cyan-400 bg-cyan-400/10'
                  : 'border-cyan-900/30 hover:border-cyan-500/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-white mb-2">拖拽图片到此处，或点击上传</p>
              <p className="text-sm text-gray-400 mb-4">支持 JPG, PNG, WebP 格式，最大 10MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg transition-colors"
              >
                选择图片
              </button>
            </div>
          )}

          {/* Preview */}
          {previewUrl && (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full max-h-96 object-contain rounded-lg border border-white/10"
                />
                {!generatedPrompt && !isUnderstanding && (
                  <button
                    onClick={() => {
                      setPreviewUrl(null)
                      setSelectedFile(null)
                    }}
                    className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Upload and Understanding Progress */}
              {isUnderstanding && (
                <div className="bg-[#0d1117] border border-cyan-900/30 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                    <div className="flex-1">
                      <p className="text-white font-medium">正在理解图片内容...</p>
                      <p className="text-sm text-gray-400">这可能需要几秒钟</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Generated Prompt */}
              {generatedPrompt && (
                <div className="bg-[#0d1117] border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white font-medium mb-2">生成的提示词</p>
                      <p className="text-gray-300 text-sm leading-relaxed">{generatedPrompt}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quota Warning */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-400 text-sm">
                  图片理解将消耗 <strong>1 个配额</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-cyan-900/30">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-cyan-900/20 hover:bg-cyan-900/30 text-white rounded-lg transition-colors"
          >
            取消
          </button>
          {!generatedPrompt && selectedFile && !isUnderstanding && (
            <button
              onClick={handleUploadAndUnderstand}
              disabled={isUploading}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-700 disabled:to-gray-700 text-white rounded-lg font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>理解中...</span>
                </>
              ) : (
                <>
                  <ImageIcon className="w-4 h-4" />
                  <span>开始理解</span>
                </>
              )}
            </button>
          )}
          {generatedPrompt && (
            <button
              onClick={handleUsePrompt}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>使用此提示词</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
